import { getBomProductPicks, getProductsWithBom, type BomProductPick } from '@api/bom-management';
import { getOrgConfig } from '@api/organization';
import {
    getVendor,
    getVendorLanes,
    listProductsForVendor,
    type VendorProductPick,
} from '@api/vendors';
import {
    createProductionOrder,
    getProductionOrder,
    listVendorsForPicker,
    listWarehousesForPicker,
    uploadWorkOrderAttachment,
    type WorkOrderLocationType,
    type VendorPickItem,
    type WarehousePickItem,
    peekNextProductionOrderCode,
    updateProductionOrder,
} from '@api/work-order';
import AddVendorModal from '@components/containers/EntityMaster/AddVendorModal';
import DocumentAuthoringShell from '@components/ui/DocumentAuthoringShell';
import QuantityInput from '@components/ui/QuantityInput';
import {
    GST_STATE_OPTIONS,
    gstStateCodeFromStateName,
    placeOfSupplyFromGstin,
} from '@diagon-alley/common';
import { DateSinglePicker } from '@components/ui/DateRange';
import { addDays, format as formatDate } from 'date-fns';
import useToaster from '@hooks/toaster/useToaster';
import { useAgreedLinePrices } from '@hooks/useAgreedLinePrices';
import { usePlaceOfSupplyFromBillTo } from '@hooks/usePlaceOfSupplyFromBillTo';
import {
    ActionIcon,
    Button,
    Checkbox,
    FileButton,
    Group,
    NumberInput,
    Radio,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { PaperclipIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';

import { getOrganizationIdFromStorage } from '../procurementOrders/formComponents';
import {
    CURRENCY_OPTIONS,
    FieldLabel,
    SectionHeader,
    todayIso,
} from '../procurementOrders/shared';

import AddPrefixModal from './AddPrefixModal';
import ProductionOrderPreview, { type PreviewLine } from './ProductionOrderPreview';
import classes from './NewProductionOrderModal.module.css';
import { formatCount } from '@utils/currency';

export type NewWorkOrderPrefillLine = {
    productId: string;
    quantity: number;
    /** Shown until the product lookups resolve; falls back to the id. */
    label?: string;
};

export type NewWorkOrderPrefill = {
    lines: NewWorkOrderPrefillLine[];
    dueDate?: string;
    locationType?: WorkOrderLocationType;
    locationId?: string | null;
};

type Props = {
    opened: boolean;
    onClose: () => void;
    /** Ids of the work order lines created under the order — one per SKU. */
    onCreated?: (workOrderIds: string[]) => void;
    /** When provided + opened, seeds the form. Use for handoffs from other views. */
    prefill?: NewWorkOrderPrefill | null;
    /**
     * The DRAFT order to edit, when the modal opens on an existing one rather than a blank.
     *
     * THE SAME FORM, NOT A SECOND ONE. A draft is corrected in the shell it was written in, with
     * the document beside it, so what the manufacturer will read is never a guess. Who makes it
     * and what it is numbered are locked — either of those changing is a new order, not an
     * edit — and everything else, lines included, is open until the order is sent.
     */
    editOrderId?: string | null;
};

/**
 * A line as it is being typed, which is not the same as a line.
 *
 * THE FIELDS HOLD STRINGS AS WELL AS NUMBERS, and that is the point. A number input emits the
 * half-finished text on every keystroke - '241.' is what sits in the box between the '.' and the
 * '5' - and it is not a number yet. Discarding those intermediates makes a decimal impossible to
 * type: the controlled value snaps back on each keystroke, so the '.' is swallowed and the rate
 * ends up empty or, worse, 24150. Keep whatever was typed and coerce at the edges instead.
 */
type LineDraft = {
    productId: string;
    quantity: number | string;
    /** Blank on an in-house run, and blank means "no rate agreed" on a priced one. */
    unitPrice: number | string;
    /** Blank is "no rate stated", which is NOT the exempt 0% slab. */
    gstRate: number | string;
    /** Only known for prefilled products that aren't in the pickers yet. */
    fallbackLabel?: string;
    /** The site has no agreed rate on file for this product, so the rate is typed, not confirmed. */
    noAgreedPrice?: boolean;
    /** The form filled the rate in from the agreed prices, so it follows quantity and currency until typed over. */
    priceAuto?: boolean;
    /** Only when something needs acting on: below the minimum, or agreed in another currency. */
    priceNote?: string;
    priceWarn?: boolean;
    /** Which agreed line the rate is, for the tooltip. */
    priceDetail?: string;
};

const emptyLine = (): LineDraft => ({
    productId: '',
    quantity: '',
    unitPrice: '',
    gstRate: '',
});

/**
 * Opens a PRODUCTION ORDER against one manufacturer — always, whether it carries one SKU or
 * ten. The work orders beneath it are bookkeeping, not a separate thing the operator asks
 * for: each SKU needs its own row so batches can be raised against it, since production runs
 * one SKU at a time. So the form only ever talks about the order.
 *
 * TWO PANES, BECAUSE THIS IS A DOCUMENT. A production order is what a brand hands a
 * manufacturer and what both sides argue from later, so it is written with the page in view
 * rather than typed into a form and discovered after it is sent. Same shell the purchase order
 * is written in.
 *
 * MONEY ONLY FOR AN OUTSOURCED RUN. An order placed on the brand's own warehouse has no
 * counterparty, nothing to bill and no rate to agree, so the whole commercial half of the form
 * disappears rather than sitting there asking to be left blank.
 */
/**
 * Not a vendor id — the row that ends the picker and opens the create form.
 *
 * Prefixed so it cannot collide with a real code: vendor ids are derived from names and hold
 * only letters, digits and underscores.
 */
const CREATE_VENDOR_VALUE = '__create_manufacturer__';

/** Same idea for the prefix picker: the row that ends the list and opens the add form. */
const ADD_PREFIX_VALUE = '__add_prefix__';

const NewWorkOrderModal: FC<Props> = ({ opened, onClose, onCreated, prefill, editOrderId }) => {
    const isEditing = Boolean(editOrderId);
    const { showToastSuccess, showToastError } = useToaster();
    const queryClient = useQueryClient();

    const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
    const [dueDate, setDueDate] = useState<string>('');
    /**
     * Same contract as placeOfSupplyPicked below: a date somebody typed is a promise and is
     * never overwritten, while a seeded one re-derives as the lane facts change.
     */
    const [completeByPicked, setCompleteByPicked] = useState(false);
    const [orderDate, setOrderDate] = useState<string>(todayIso());
    const [locationType, setLocationType] = useState<WorkOrderLocationType>('VENDOR');
    const [locationId, setLocationId] = useState<string | null>(null);
    /** Collapsed to a summary once the details are known; open while they are being typed. */
    const [isEditingManufacturer, setIsEditingManufacturer] = useState(false);
    const [isCreatingVendor, setIsCreatingVendor] = useState(false);
    /** Which stem the number was built from. Only ever the picker's own state. */
    const [numberPrefix, setNumberPrefix] = useState<string | null>(null);
    const [isAddingPrefix, setIsAddingPrefix] = useState(false);
    /** What is typed in the location picker, purely so the empty state can hold its tongue. */
    const [locationSearch, setLocationSearch] = useState('');
    const [notes, setNotes] = useState<string>('');
    const [terms, setTerms] = useState<string>('');
    const [currency, setCurrency] = useState<string>('INR');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /** Off by default, which is the ask: offer what this site is mapped to make. */
    const [showAllProducts, setShowAllProducts] = useState(false);

    const [manufacturerName, setManufacturerName] = useState('');
    const [manufacturerEmail, setManufacturerEmail] = useState('');
    const [manufacturerAddress, setManufacturerAddress] = useState('');
    const [manufacturerGstin, setManufacturerGstin] = useState('');
    const [billToName, setBillToName] = useState('');
    const [billToAddress, setBillToAddress] = useState('');
    const [billToGstin, setBillToGstin] = useState('');
    const [shipToWarehouseId, setShipToWarehouseId] = useState<string | null>(null);
    const [shipToName, setShipToName] = useState('');
    const [shipToEmail, setShipToEmail] = useState('');
    const [shipToAddress, setShipToAddress] = useState('');
    const [shipToGstin, setShipToGstin] = useState('');
    const [shipToStateCode, setShipToStateCode] = useState<string | null>(null);
    /** True once somebody picks a state by hand, after which the GSTIN stops driving it. */
    const [placeOfSupplyPicked, setPlaceOfSupplyPicked] = useState(false);
    /** Held by hand or by the Bill To GSTIN; the Ship To side seeds the state only while false. */
    const placeOfSupplyLocked = usePlaceOfSupplyFromBillTo(billToGstin, placeOfSupplyPicked, setShipToStateCode);
    /** Blank means "let the server number it", which is what almost every order will do. */
    const [code, setCode] = useState('');
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');

    const isOutsourced = locationType === 'VENDOR';

    const bomProductsQuery = useQuery<BomProductPick[]>({
        queryKey: ['products-with-bom'],
        queryFn: getProductsWithBom,
        enabled: opened,
    });

    const allProductsQuery = useQuery<BomProductPick[]>({
        queryKey: ['bom-product-picks'],
        queryFn: getBomProductPicks,
        enabled: opened,
    });

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['wo-warehouses'],
        queryFn: listWarehousesForPicker,
        // Fetched for both modes now: an outsourced order still ships its output to one of
        // the brand's own sites, so Ship To needs the list even when production does not.
        enabled: opened,
    });

    /**
     * The manufacturer's lanes - where it ships, and how long door to door. They narrow the
     * Ship To picker to destinations the brand has actually recorded, and their summed legs
     * seed the Complete-by date. A vendor with no lanes changes nothing - the full list and
     * a hand-picked date remain, which is every order before lanes existed.
     */
    const lanesQuery = useQuery({
        queryKey: ['vendor-lanes', locationId],
        queryFn: () => getVendorLanes(locationId as string),
        enabled: opened && locationType === 'VENDOR' && !!locationId,
    });

    /**
     * The manufacturer's own details, seeded from the master and then owned by this document.
     *
     * SEEDED, NOT BOUND. The order keeps its own copy of the name, email, address and GSTIN
     * precisely so a later change to the vendor cannot rewrite a document that has already been
     * sent. Picking a manufacturer fills those boxes once; editing them afterwards changes this
     * order and nothing else, and does not write back to the master. That is deliberate — an
     * address book that any document screen can quietly amend is an address book nobody trusts.
     */
    const applyVendorDetails = (vendor: {
        name?: string;
        legalName?: string | null;
        email?: string;
        street?: string;
        city?: string;
        zip?: string;
        country?: string;
        taxCode?: string;
    }) => {
        // Blank parts drop out, so a vendor holding only a city does not print ", , , ".
        const address = [vendor.street, vendor.city, vendor.zip, vendor.country]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(', ');

        // THE REGISTERED COMPANY WINS, falling back to the working name. The GSTIN and the
        // address on this document belong to the legal entity, so naming the party "Apricot"
        // above "Apricot Foods Private Limited"'s GSTIN would print a document addressed to
        // somebody who does not, strictly, exist.
        setManufacturerName(vendor.legalName?.trim() || vendor.name?.trim() || '');
        setManufacturerEmail(vendor.email?.trim() || '');
        setManufacturerAddress(address);
        setManufacturerGstin(vendor.taxCode?.trim() || '');

        // Open only when there was nothing to fill. Most vendors on record carry a name and
        // little else, and a collapsed summary of three empty lines would hide the work still
        // to do rather than save anybody from it.
        setIsEditingManufacturer(!(vendor.email || address || vendor.taxCode));
    };

    const vendorsQuery = useQuery<VendorPickItem[]>({
        queryKey: ['wo-vendors'],
        queryFn: listVendorsForPicker,
        enabled: opened && isOutsourced,
    });

    /** What this manufacturer is mapped to make, with the terms already agreed for it. */
    const vendorProductsQuery = useQuery<VendorProductPick[]>({
        queryKey: ['vendor-products', locationId],
        queryFn: () => listProductsForVendor(locationId as string),
        enabled: opened && isOutsourced && !!locationId,
    });

    /** The order being edited, with its live lines and the rate agreed on each. */
    const editQuery = useQuery({
        queryKey: ['production-order', editOrderId],
        queryFn: () => getProductionOrder(editOrderId as string),
        enabled: opened && !!editOrderId,
    });

    /**
     * Seeded ONCE per opening, from what is stored.
     *
     * Every box takes the saved value, blank included — a blank GSTIN on the draft is a blank
     * GSTIN, not an invitation to fill one in from the master. The two "picked" guards are set so
     * the lane and GSTIN helpers below leave the loaded date and place of supply alone: those
     * were somebody's decision when the draft was written, and reopening it must not undo them.
     */
    const seededEdit = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededEdit.current = false;
            return;
        }
        if (!editOrderId || seededEdit.current || !editQuery.data) return;
        seededEdit.current = true;

        const { order, lines: stored } = editQuery.data;
        const dateOnly = (value: string | null | undefined) =>
            value ? String(value).slice(0, 10) : '';

        setLines(
            stored.length > 0
                ? stored.map((line) => ({
                      productId: line.productId,
                      quantity: line.quantity,
                      unitPrice: line.unitPrice ?? '',
                      gstRate: line.gstRate ?? '',
                  }))
                : [emptyLine()],
        );
        setLocationType(order.productionLocationType);
        setLocationId(order.productionLocationId);
        setCode(order.code);
        setOrderDate(dateOnly(order.orderDate) || todayIso());
        setDueDate(dateOnly(order.expectedCompletionDate));
        setCompleteByPicked(true);
        setCurrency(order.currency ?? 'INR');
        setNotes(order.notes ?? '');
        setTerms(order.termsAndConditions ?? '');
        setManufacturerName(order.manufacturerName ?? '');
        setManufacturerEmail(order.manufacturerEmail ?? '');
        setManufacturerAddress(order.manufacturerAddress ?? '');
        setManufacturerGstin(order.manufacturerGstin ?? '');
        setIsEditingManufacturer(false);
        setBillToName(order.billToName ?? '');
        setBillToAddress(order.billToAddress ?? '');
        setBillToGstin(order.billToGstin ?? '');
        setShipToWarehouseId(order.shipToWarehouseId ?? null);
        setShipToName(order.shipToName ?? '');
        setShipToEmail(order.shipToEmail ?? '');
        setShipToAddress(order.shipToAddress ?? '');
        setShipToGstin(order.shipToGstin ?? '');
        setShipToStateCode(order.shipToStateCode ?? null);
        setPlaceOfSupplyPicked(Boolean(order.shipToStateCode));
        setContactName(order.contactName ?? '');
        setContactEmail(order.contactEmail ?? '');
    }, [opened, editOrderId, editQuery.data]);

    // Seed from prefill on the open transition only, so later edits aren't overwritten.
    useEffect(() => {
        if (!opened || !prefill || isEditing) return;
        setLines(
            prefill.lines.map((line) => ({
                productId: line.productId,
                quantity: line.quantity,
                unitPrice: '',
                gstRate: '',
                fallbackLabel: line.label,
                // A prefilled line has no rate yet; the agreed one fills in for its quantity.
                priceAuto: true,
            })),
        );
        setDueDate(prefill.dueDate ?? '');
        if (prefill.locationType) setLocationType(prefill.locationType);
        if (prefill.locationId !== undefined) setLocationId(prefill.locationId);
    }, [opened, prefill, isEditing]);

    const reset = () => {
        setLines([emptyLine()]);
        setDueDate('');
        setCompleteByPicked(false);
        setOrderDate(todayIso());
        setLocationType('VENDOR');
        setLocationId(null);
        setNotes('');
        setTerms('');
        setCurrency('INR');
        setAttachments([]);
        setShowAllProducts(false);
        setNumberPrefix(null);
        setIsEditingManufacturer(false);
        setManufacturerName('');
        setManufacturerEmail('');
        setManufacturerAddress('');
        setManufacturerGstin('');
        setBillToName('');
        setBillToAddress('');
        setBillToGstin('');
        setShipToWarehouseId(null);
        setShipToName('');
        setShipToEmail('');
        setShipToAddress('');
        setShipToGstin('');
        setShipToStateCode(null);
        setPlaceOfSupplyPicked(false);
        setCode('');
        setContactName('');
        setContactEmail('');
        setIsSubmitting(false);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        reset();
        onClose();
    };

    const handleLocationTypeChange = (value: string) => {
        setLocationType(value as WorkOrderLocationType);
        setLocationId(null);
        // The block belongs to whoever was picked. Leaving one manufacturer's GSTIN behind
        // while the order now names another is how a document goes out addressed to the wrong
        // company.
        setIsEditingManufacturer(false);
        setManufacturerName('');
        setManufacturerEmail('');
        setManufacturerAddress('');
        setManufacturerGstin('');
    };

    /** Picking a manufacturer seeds the whole block from the master - see applyVendorDetails. */
    const handleVendorPicked = (id: string | null) => {
        // The last row of the list is a door, not a destination.
        if (id === CREATE_VENDOR_VALUE) {
            setIsCreatingVendor(true);
            return;
        }

        setLocationId(id);
        if (!id) return;

        // The name is known immediately, so it lands without waiting on the network - clearing
        // the picker and watching the block stay blank for a beat reads as a failure.
        const listed = vendorsQuery.data?.find((v) => v.vendorId === id);
        setManufacturerName(listed?.name || id);

        /*
         * The rest comes from the single-vendor read, because the LIST endpoint carries city
         * and country but neither street, zip nor GSTIN.
         *
         * A failure here is silent on purpose. The name is already in, every field stays
         * editable, and the form is no worse off than before this existed - a red banner over a
         * detail somebody can simply type would be the louder problem.
         */
        void getVendor(id)
            .then((vendor) => applyVendorDetails({ ...vendor, name: vendor.name || id }))
            .catch(() => setIsEditingManufacturer(true));
    };

    /**
     * Picking a warehouse fills the whole Ship To block from the master.
     *
     * THIS USED TO SEED ONLY THE NAME, behind a comment claiming warehouses have "no address
     * column". They do, and always did - street, city, state, country, zip - the API type had
     * narrowed them away and the comment was written against the narrowed type. The address on
     * record now lands in one line, blank parts dropped.
     *
     * The state also settles PLACE OF SUPPLY - the IGST-vs-CGST switch below - matched by name
     * against the GST state list, and only when nobody has picked one by hand: same
     * filled-not-forced rule the GSTIN handler follows, because where goods land is a legal
     * fact the master data does not always know.
     *
     * The GST NUMBER is deliberately NOT seeded. Warehouses carry no GSTIN, and the org's
     * letterhead GSTIN would be wrong exactly when it matters - a warehouse in another state
     * sits under a different registration.
     *
     * Every field stays editable afterwards: the place goods actually go is often a gate or a
     * dock, not the site as the master records it.
     */
    /** Laned destinations for the picked manufacturer; null = unrestricted (no lanes). */
    const lanedWarehouseIds = useMemo(() => {
        const lanes = lanesQuery.data ?? [];
        if (locationType !== 'VENDOR' || lanes.length === 0) return null;
        return new Set(lanes.map((lane) => lane.warehouseId));
    }, [lanesQuery.data, locationType]);

    const shipToOptions = useMemo(
        () =>
            (warehousesQuery.data ?? [])
                .filter((w) => !lanedWarehouseIds || lanedWarehouseIds.has(w.warehouseId))
                .map((w) => ({ value: w.warehouseId, label: w.name })),
        [warehousesQuery.data, lanedWarehouseIds],
    );

    /*
     * Complete-by, seeded from the lane: order date plus the door-to-door legs (production,
     * QC, transit, delivery). Re-derives when the manufacturer, destination or order date
     * changes; a hand-picked date is a promise and is never overwritten. Several lanes for
     * one pair take the shortest total - the optimistic reading, and deterministic.
     */
    useEffect(() => {
        if (completeByPicked || !shipToWarehouseId || !orderDate) return;

        const totals = (lanesQuery.data ?? [])
            .filter((lane) => lane.warehouseId === shipToWarehouseId)
            .map(
                (lane) =>
                    (lane.productionTime ?? 0)
                    + (lane.qcTime ?? 0)
                    + (lane.transitTime ?? 0)
                    + (lane.deliveryTime ?? 0),
            )
            .filter((total) => total > 0);
        if (totals.length === 0) return;

        const start = new Date(orderDate);
        if (Number.isNaN(start.getTime())) return;
        setDueDate(formatDate(addDays(start, Math.min(...totals)), 'yyyy-MM-dd'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completeByPicked, shipToWarehouseId, orderDate, lanesQuery.data]);

    const handleShipToWarehouse = (id: string | null) => {
        setShipToWarehouseId(id);
        const warehouse = warehousesQuery.data?.find((w) => w.warehouseId === id);

        /*
         * CLEARING THE PICKER CLEARS WHAT IT SEEDED. The early-return version left the previous
         * warehouse's name and address standing after the X, which read as the form ignoring
         * the clear - and would have shipped an order to a place nobody currently named. Only
         * the seeded trio goes; a hand-picked place of supply survives, same rule as filling.
         */
        if (!warehouse) {
            setShipToName('');
            setShipToAddress('');
            if (!placeOfSupplyLocked) setShipToStateCode(null);
            return;
        }

        setShipToName(warehouse.name || warehouse.warehouseId);

        const address = [warehouse.street, warehouse.city, warehouse.zipCode, warehouse.country]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(', ');
        // Set unconditionally: switching to an address-less warehouse must not keep the
        // previous one's address standing under the new name.
        setShipToAddress(address);

        if (!placeOfSupplyLocked) {
            const seededState = gstStateCodeFromStateName(warehouse.state);
            if (seededState) setShipToStateCode(seededState);
        }
    };

    /*
     * A picked warehouse that falls off-lane when the manufacturer changes clears through
     * the handler, so the seeded name and address leave with it rather than lingering as an
     * address nobody currently named.
     */
    useEffect(() => {
        if (!shipToWarehouseId || !lanedWarehouseIds) return;
        if (!lanedWarehouseIds.has(shipToWarehouseId)) handleShipToWarehouse(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lanedWarehouseIds]);


    /**
     * The brand's standard terms, which seed this order's.
     *
     * Same query key the preview uses, so this costs no extra request.
     */
    const organizationId = useMemo(() => getOrganizationIdFromStorage(), []);

    /**
     * The brand's own order-number stems, if it has defined any.
     *
     * Absent for every brand that has never opened Settings > Order Numbering, and the control
     * hides itself entirely in that case - the field goes on reading "Auto-generated" exactly as
     * it did, and the server keeps applying `PO-{year}-`.
     */
    const numberingQuery = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: opened && !!organizationId,
        staleTime: 5 * 60 * 1000,
    });

    const prefixes = useMemo(
        () =>
            (numberingQuery.data?.documentNumbering?.PRODUCTION_ORDER?.prefixes ?? []).filter(
                (entry) => entry.value?.trim(),
            ),
        [numberingQuery.data],
    );

    /**
     * Fills the number in as soon as a stem is chosen, which is the whole of the feature.
     *
     * The server works out what comes next; asking it beats counting on the client, which cannot
     * see the orders somebody else raised this morning. A failure leaves the prefix in the box on
     * its own - still a usable start, and the server completes it at save.
     */
    const applyPrefix = (prefix: string | null) => {
        setNumberPrefix(prefix);
        if (!prefix) return;

        const padWidth = numberingQuery.data?.documentNumbering?.PRODUCTION_ORDER?.padWidth;
        void peekNextProductionOrderCode(prefix, padWidth)
            .then((next) => setCode(next || prefix))
            .catch(() => setCode(prefix));
    };

    const orgQuery = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: !!organizationId,
    });

    /**
     * Seeded ONCE per opening, and only into an untouched box.
     *
     * Not "fill whenever the field is empty": somebody who deliberately clears the terms for a
     * one-off order would have them put straight back, on the next render that resolves the org.
     * The guard resets when the modal closes, so the next new order starts from the default again.
     */
    const seededTerms = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededTerms.current = false;
            return;
        }
        // An existing draft's terms are its own, blank or not; the default is for new orders.
        if (isEditing || seededTerms.current || !orgQuery.data) return;
        seededTerms.current = true;
        setTerms((current) => current || (orgQuery.data.documentTerms ?? ''));
    }, [opened, orgQuery.data, isEditing]);

    /**
     * Bill To is this brand billing itself, so it is the same three values on every order.
     *
     * Seeded on the same terms as the standard terms above — once per opening, only into a box
     * nobody has touched — because the two answer the same objection: an operator retyping their
     * own company's registered name and GSTIN on every order will eventually mistype one, and a
     * GST document whose party name does not match the GSTIN beside it is wrong.
     *
     * `legalName` is the registered entity and `name` the trading one; the document wants the
     * first and falls back to the second, which is all a brand that has not filled the
     * letterhead in yet has to offer.
     */
    const seededBillTo = useRef(false);
    useEffect(() => {
        if (!opened) {
            seededBillTo.current = false;
            return;
        }
        if (isEditing || seededBillTo.current || !orgQuery.data) return;
        seededBillTo.current = true;
        const org = orgQuery.data;
        setBillToName((current) => current || org.legalName || org.name || '');
        setBillToAddress((current) => current || (org.address ?? ''));
        setBillToGstin((current) => current || (org.gstin ?? ''));
    }, [opened, orgQuery.data, isEditing]);

    /**
     * A GSTIN states where the party is, so typing one fills in the place of supply.
     *
     * THE FIRST TWO DIGITS OF A GSTIN ARE ITS STATE CODE, which is the same fact the document
     * uses to read the manufacturer's own state. Making somebody type the number and then pick
     * the state it already contains is how an order ends up with no place of supply and a
     * document that can only print an undifferentiated "Tax".
     *
     * FILLED, NOT FORCED, and only while nothing stronger holds the place of supply: a state
     * chosen by hand wins and is never overwritten, and so does the Bill To GSTIN, which names
     * the place of supply whenever there is one (usePlaceOfSupplyFromBillTo). Until then this
     * GSTIN keeps driving it, so correcting a mistyped number corrects the tax with it rather
     * than leaving a stale state behind.
     */
    const handleShipToGstin = (value: string) => {
        setShipToGstin(value);
        if (placeOfSupplyLocked) return;

        // Null for a half-typed or placeholder number, which must not set a tax treatment.
        const derived = placeOfSupplyFromGstin(value);
        if (derived) setShipToStateCode(derived);
    };

    const bomProductIds = useMemo(
        () => new Set((bomProductsQuery.data ?? []).map((p) => p.productId)),
        [bomProductsQuery.data],
    );

    /**
     * Name and code per product, kept apart.
     *
     * The picker's option label joins them for display; the preview needs them separately, and
     * splitting the label back apart on an em dash would break on any product whose name
     * contains one.
     */
    const productMeta = useMemo(() => {
        const byId = new Map<
            string,
            { sku: string | null; name: string; hsnCode: string | null; unitsPerCase: number | null }
        >();
        for (const p of allProductsQuery.data ?? []) {
            if (p.productId) {
                byId.set(p.productId, {
                    sku: p.sku,
                    name: p.name ?? p.productId,
                    // The catalogue picker does not carry it; the document reads it at render.
                    hsnCode: null,
                    unitsPerCase: p.unitsPerCase ?? null,
                });
            }
        }
        for (const p of vendorProductsQuery.data ?? []) {
            if (p.productId) {
                byId.set(p.productId, {
                    sku: p.sku,
                    name: p.name,
                    hsnCode: p.hsnCode,
                    unitsPerCase: p.unitsPerCase ?? null,
                });
            }
        }
        return byId;
    }, [allProductsQuery.data, vendorProductsQuery.data]);

    /** Whether any product on offer has a case size, which is when the Qty column makes room for the toggle. */
    const casePackOnOffer = useMemo(
        () => [...productMeta.values()].some((meta) => meta.unitsPerCase),
        [productMeta],
    );
    const lineQtyClass = casePackOnOffer ? `${classes.lineQty} ${classes.lineQtyWide}` : classes.lineQty;

    const labelFor = useMemo(() => {
        const byId = new Map<string, string>();
        for (const [productId, meta] of productMeta) {
            // Name first, product code second - the same label the picker builds, so a line
            // reads identically whether it was just picked or came in from a prefill.
            byId.set(productId, meta.name ? `${meta.name} — ${productId}` : productId);
        }
        return byId;
    }, [productMeta]);

    /** The agreed terms per product for the selected manufacturer, for filling a picked line. */
    const vendorTerms = useMemo(
        () => new Map((vendorProductsQuery.data ?? []).map((p) => [p.productId, p])),
        [vendorProductsQuery.data],
    );

    /**
     * What the picker offers.
     *
     * MAPPED FIRST, NOT MAPPED ONLY. Offering what a site is mapped to make is how the screen
     * stops somebody commissioning a jersey from a box supplier. A hard filter would be worse
     * than the problem: most manufacturers have no mappings yet, so a newly onboarded one would
     * be impossible to order from until somebody went and built its catalogue. The checkbox is
     * the way past it, and it appears only when there is something to be past.
     */
    const productOptions = useMemo(() => {
        const mapped = vendorProductsQuery.data ?? [];
        const useMapped = isOutsourced && !showAllProducts && mapped.length > 0;

        const source = useMapped
            ? mapped.map((p) => ({ productId: p.productId, sku: p.sku, name: p.name }))
            : isOutsourced
              ? (allProductsQuery.data ?? [])
              : (bomProductsQuery.data ?? []);

        // One line per product: what is already on the order is greyed in the dropdown, so a
        // second line cannot be opened for it. The backend rejects duplicates on save anyway;
        // this says so before the save.
        const picked = new Set(lines.map((l) => l.productId).filter(Boolean));
        const options = source
            .filter((p) => p.productId)
            .map((p) => ({
                value: p.productId,
                disabled: picked.has(p.productId),
                /*
                 * NAME PLUS THE PRODUCT CODE, NOT THE BARCODE. On merchants whose sku column
                 * carries the EAN (Troovy), "Moong dal chips - 8905304001888" made people stop
                 * and check; MOONG_200 is the code they actually recognise. The trade is that
                 * pasting a barcode into the search no longer matches - the code and the name
                 * do, which is how this picker is actually used.
                 */
                label: p.name ? `${p.name} — ${p.productId}` : p.productId,
            }));

        // Prefilled or already-picked products can sit outside the current option set — a MAKE
        // SKU with no BOM while the form is in-house, or one the vendor is not mapped to. Keep
        // them selectable so the picker never silently drops a line somebody was handed.
        const known = new Set(options.map((o) => o.value));
        for (const line of lines) {
            if (!line.productId || known.has(line.productId)) continue;
            options.push({
                value: line.productId,
                label: labelFor.get(line.productId) ?? line.fallbackLabel ?? line.productId,
                disabled: true,
            });
        }
        return options;
    }, [
        isOutsourced,
        showAllProducts,
        vendorProductsQuery.data,
        allProductsQuery.data,
        bomProductsQuery.data,
        lines,
        labelFor,
    ]);

    const locationOptions = isOutsourced
        ? [
              ...(vendorsQuery.data?.map((v) => ({ value: v.vendorId, label: v.name })) ?? []),
              { value: CREATE_VENDOR_VALUE, label: '+ Create manufacturer' },
          ]
        : (warehousesQuery.data?.map((w) => ({ value: w.warehouseId, label: w.name })) ?? []);

    const updateLine = (index: number, patch: Partial<LineDraft>) =>
        setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

    /** Picking a product fills the terms this manufacturer already has for it. */
    const handleProductPicked = (index: number, productId: string | null) => {
        if (productId) {
            const already = lines.findIndex((l, i) => i !== index && l.productId === productId);
            if (already !== -1) {
                showToastError({
                    title: 'Already on this order',
                    subtitle: `Edit the quantity on line ${already + 1} instead.`,
                });
                return;
            }
        }
        const terms = productId ? vendorTerms.get(productId) : undefined;
        updateLine(index, {
            productId: productId ?? '',
            // Only suggestions, and only where a figure exists — a mapping without a price
            // must not fill in a zero somebody would then have to notice and clear.
            ...(terms?.price != null ? { unitPrice: terms.price } : {}),
            ...(terms?.gstRate != null ? { gstRate: terms.gstRate } : {}),
            noAgreedPrice: !!productId && isOutsourced && terms?.price == null,
            // From here the rate follows the line's quantity and the order's currency, until typed over.
            priceAuto: true,
            priceNote: undefined,
            priceWarn: false,
            priceDetail: undefined,
        });
    };

    /*
     * The manufacturer's agreed rate follows the line's quantity (tiers) and the order's
     * currency, for every line whose rate the form filled in. Production orders carry no
     * payment terms option, so the manufacturer's default terms apply. A typed rate, or one
     * loaded from a saved order, is left alone.
     */
    useAgreedLinePrices({
        enabled: opened && isOutsourced,
        vendorId: isOutsourced ? locationId : null,
        currency,
        termsOptionId: null,
        lines: lines.flatMap((l) =>
            l.priceAuto && l.productId
                ? [{
                      key: l.productId,
                      itemType: 'product' as const,
                      itemId: l.productId,
                      quantity: l.quantity === '' || !Number.isFinite(Number(l.quantity)) ? undefined : Number(l.quantity),
                  }]
                : [],
        ),
        onResolved: (results) => {
            const byProduct = new Map(results.map((r) => [r.key, r]));
            setLines((prev) =>
                prev.map((l) => {
                    const r = byProduct.get(l.productId);
                    const quantity = l.quantity === '' || !Number.isFinite(Number(l.quantity)) ? undefined : Number(l.quantity);
                    if (!r || !l.priceAuto || r.quantity !== quantity) return l;
                    return { ...l, ...r.patch };
                }),
            );
        },
    });

    const removeLine = (index: number) =>
        setLines((prev) => (prev.length === 1 ? [emptyLine()] : prev.filter((_, i) => i !== index)));

    const num = (value: number | string) => Number(value) || 0;

    /**
     * Whether a figure was actually stated, as opposed to left blank or half-typed.
     *
     * A BLANK RATE AND A 0% RATE ARE DIFFERENT CLAIMS - 0% is the exempt slab and blank is
     * "nobody said" - so this cannot just be a truthiness test. It also has to reject a lone
     * '.' mid-keystroke, which would otherwise be sent as a stated zero and print an exemption
     * on the document that nobody claimed.
     */
    const stated = (value: number | string) =>
        value !== '' && Number.isFinite(Number(value));

    const validLines = lines.filter(
        (line) => line.productId && Number.isFinite(num(line.quantity)) && num(line.quantity) > 0,
    );

    // In-house production allocates materials, so it genuinely needs the BOM; the backend
    // rejects those lines. Say so here rather than letting the submit fail.
    const missingBomLines = isOutsourced
        ? []
        : validLines.filter(
              (line) => !bomProductsQuery.isLoading && !bomProductIds.has(line.productId),
          );

    const duplicateSku = useMemo(() => {
        const seen = new Set<string>();
        for (const line of validLines) {
            if (seen.has(line.productId)) return line.productId;
            seen.add(line.productId);
        }
        return null;
    }, [validLines]);

    const canSubmit =
        validLines.length > 0
        && missingBomLines.length === 0
        && !duplicateSku
        && !!locationId
        && !!dueDate
        && !(isEditing && !editQuery.data)
        && !isSubmitting;

    const totalUnits = validLines.reduce((sum, line) => sum + num(line.quantity), 0);

    const previewLines: PreviewLine[] = lines.map((line) => ({
        productId: line.productId,
        productName: productMeta.get(line.productId)?.name ?? line.fallbackLabel ?? null,
        productSku: productMeta.get(line.productId)?.sku ?? null,
        hsnCode: productMeta.get(line.productId)?.hsnCode ?? null,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        gstRate: line.gstRate,
    }));

    /**
     * What goes on the wire for a text box in edit mode: the trimmed value, or null to clear.
     *
     * A PATCH cannot say "blank" with a blank — the server's string fields refuse an empty one —
     * so a cleared box is sent as null, which the server writes as NULL. Always sent, never
     * omitted: the form shows every field, so every field is a statement.
     */
    const patchText = (value: string) => value.trim() || null;

    const lineInputs = () =>
        validLines.map((line) => ({
            productId: line.productId,
            quantity: num(line.quantity),
            // Sent only for an outsourced run, and only where a figure was entered:
            // an omitted rate records "not stated", which is not a zero.
            ...(isOutsourced && stated(line.unitPrice) ? { unitPrice: num(line.unitPrice) } : {}),
            ...(isOutsourced && stated(line.gstRate) ? { gstRate: num(line.gstRate) } : {}),
        }));

    /**
     * Whether the SKU set differs from what was loaded.
     *
     * SENT ONLY WHEN IT DOES. The server replaces the line set whole — every SKU is re-created and
     * the old row cancelled — so sending it on a header-only edit would renumber every line and
     * orphan whatever was attached to them, for no change anybody asked for.
     */
    const linesChanged = () => {
        const stored = editQuery.data?.lines ?? [];
        const next = lineInputs();
        if (stored.length !== next.length) return true;
        const byProduct = new Map(stored.map((line) => [line.productId, line]));
        return next.some((line) => {
            const was = byProduct.get(line.productId);
            if (!was) return true;
            return (
                line.quantity !== was.quantity
                || (line.unitPrice ?? null) !== (was.unitPrice ?? null)
                || (line.gstRate ?? null) !== (was.gstRate ?? null)
            );
        });
    };

    const handleSubmit = async () => {
        if (!canSubmit || !locationId) return;
        setIsSubmitting(true);
        try {
            const order = isEditing && editOrderId
                ? await updateProductionOrder(editOrderId, {
                      expectedCompletionDate: dueDate,
                      orderDate,
                      notes: patchText(notes),
                      ...(linesChanged() ? { lines: lineInputs() } : {}),
                      ...(isOutsourced
                          ? {
                                currency,
                                termsAndConditions: patchText(terms),
                                manufacturerName: patchText(manufacturerName),
                                manufacturerEmail: patchText(manufacturerEmail),
                                manufacturerAddress: patchText(manufacturerAddress),
                                manufacturerGstin: patchText(manufacturerGstin),
                                billToName: patchText(billToName),
                                billToAddress: patchText(billToAddress),
                                billToGstin: patchText(billToGstin),
                                shipToName: patchText(shipToName),
                                shipToEmail: patchText(shipToEmail),
                                shipToAddress: patchText(shipToAddress),
                                shipToGstin: patchText(shipToGstin),
                                shipToWarehouseId: shipToWarehouseId || null,
                                shipToStateCode: shipToStateCode || null,
                                contactName: patchText(contactName),
                                contactEmail: patchText(contactEmail),
                            }
                          : {}),
                  })
                : await createProductionOrder({
                ...(code.trim() ? { code: code.trim() } : {}),
                productionLocationType: locationType,
                productionLocationId: locationId,
                expectedCompletionDate: dueDate,
                lines: lineInputs(),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
                orderDate,
                // The commercial half goes only when there is a counterparty for it.
                ...(isOutsourced
                    ? {
                          currency,
                          ...(terms.trim() ? { termsAndConditions: terms.trim() } : {}),
                          ...(manufacturerName.trim() ? { manufacturerName: manufacturerName.trim() } : {}),
                          ...(manufacturerEmail.trim() ? { manufacturerEmail: manufacturerEmail.trim() } : {}),
                          ...(manufacturerAddress.trim() ? { manufacturerAddress: manufacturerAddress.trim() } : {}),
                          ...(manufacturerGstin.trim() ? { manufacturerGstin: manufacturerGstin.trim() } : {}),
                          ...(billToName.trim() ? { billToName: billToName.trim() } : {}),
                          ...(billToAddress.trim() ? { billToAddress: billToAddress.trim() } : {}),
                          ...(billToGstin.trim() ? { billToGstin: billToGstin.trim() } : {}),
                          ...(shipToName.trim() ? { shipToName: shipToName.trim() } : {}),
                          ...(shipToEmail.trim() ? { shipToEmail: shipToEmail.trim() } : {}),
                          ...(shipToAddress.trim() ? { shipToAddress: shipToAddress.trim() } : {}),
                          ...(shipToGstin.trim() ? { shipToGstin: shipToGstin.trim() } : {}),
                          ...(shipToWarehouseId ? { shipToWarehouseId } : {}),
                          ...(shipToStateCode ? { shipToStateCode } : {}),
                          ...(contactName.trim() ? { contactName: contactName.trim() } : {}),
                          ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
                      }
                    : {}),
            });
            const createdIds = order.workOrderIds ?? [];

            // Attachments hang off each SKU line, so the spec travels with whichever line
            // someone opens.
            const uploadFailures: string[] = [];
            for (const file of attachments) {
                for (const workOrderId of createdIds) {
                    try {
                        await uploadWorkOrderAttachment(workOrderId, file);
                    } catch {
                        uploadFailures.push(file.name);
                        break;
                    }
                }
            }

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['work-orders'] }),
                queryClient.invalidateQueries({ queryKey: ['production-orders'] }),
                queryClient.invalidateQueries({ queryKey: ['production-order', editOrderId] }),
            ]);

            showToastSuccess({
                title: `Production order ${isEditing ? 'updated' : 'created'} — ${validLines.length} ${
                    validLines.length === 1 ? 'SKU' : 'SKUs'
                }`,
                subtitle: uploadFailures.length
                    ? `Some files did not attach: ${uploadFailures.join(', ')}`
                    : isEditing && editQuery.data?.order.status === 'ISSUED'
                      ? 'PDF regenerated. Use Resend to send the manufacturer the new version.'
                      : undefined,
            });

            onCreated?.(createdIds);
            reset();
            onClose();
        } catch (err: any) {
            showToastError({
                title: isEditing
                    ? 'Could not save the production order'
                    : 'Could not create the production order',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
            setIsSubmitting(false);
        }
    };

    const mappedCount = vendorProductsQuery.data?.length ?? 0;

    /*
     * A NAME ALONE IS NOT "FILLED". Picking any manufacturer sets the name, so treating that as
     * filled would collapse the block on every order and hide three empty boxes behind a
     * summary that looked complete.
     */
    const manufacturerIsFilled = Boolean(
        manufacturerName && (manufacturerEmail || manufacturerAddress || manufacturerGstin),
    );

    return (
        <>
        <DocumentAuthoringShell
            opened={opened}
            onClose={handleClose}
            title={isEditing ? `Edit Production Order · ${code || '…'}` : 'New Production Order'}
            preview={
                <ProductionOrderPreview
                    code={code.trim()}
                    status="DRAFT"
                    currency={isOutsourced ? currency : null}
                    orderDate={orderDate}
                    expectedCompletionDate={dueDate}
                    manufacturerName={manufacturerName}
                    manufacturerAddress={manufacturerAddress}
                    manufacturerGstin={manufacturerGstin}
                    billToName={billToName}
                    billToAddress={billToAddress}
                    billToGstin={billToGstin}
                    shipToName={shipToName}
                    shipToAddress={shipToAddress}
                    shipToGstin={shipToGstin}
                    shipToStateCode={shipToStateCode}
                    lines={previewLines}
                    charges={[]}
                    notes={notes}
                    terms={terms}
                />
            }
            actions={
                <>
                    <Text size="12px" c="dimmed" mr="auto">
                        {validLines.length
                            ? `${validLines.length} ${validLines.length === 1 ? 'SKU' : 'SKUs'} · ${formatCount(totalUnits)} pcs`
                            : 'Nothing to order yet'}
                    </Text>
                    <Button variant="default" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
                        {isEditing ? 'Save changes' : 'Create production order'}
                    </Button>
                </>
            }
            form={
                <>
                    {isEditing && (
                        <Text size="12px" c="dimmed" mb={12}>
                            {editQuery.isLoading
                                ? 'Loading the order…'
                                : editQuery.data?.order.status === 'ISSUED'
                                  ? 'This order has been sent but not yet acknowledged. Saving regenerates the PDF; the manufacturer is not emailed until you Resend. Once they acknowledge, it can no longer be edited.'
                                  : 'This order has not been sent. What you change here is what the manufacturer will receive. Once they acknowledge it, it can no longer be edited.'}
                        </Text>
                    )}
                    <SectionHeader first>WHERE AND BY WHEN</SectionHeader>
                    {/* 16px rather than 12: the radio row is taller than a text input, and at 12 the
                        Manufacturer label below it read as attached to the radios rather than to
                        its own field. */}
                    <div className="space-y-4">
                        <div>
                            {/*
                              * A radio is a taller thing than a text input, so the 4px that sits
                              * a box under its label leaves a row of them crowding the question.
                              * 10 gives the three lines - heading, question, choices - room to
                              * read as three lines.
                              */}
                            {/*
                              * THE ORDER'S NAME SITS UP HERE, beside the who-produces choice,
                              * not in the date row below. Four fields shared that row and the
                              * number - a select plus a text box - was the widest thing in it;
                              * squeezed to a quarter-width the code was unreadable past six or
                              * seven characters, which is unhelpfully close to a prefix's
                              * length. The top row had a question and empty space; now the two
                              * facts a person checks first - who makes it, what it is called -
                              * are the first line of the form.
                              */}
                            <Group justify="space-between" align="flex-end" gap={16} wrap="nowrap">
                                <div>
                                    <FieldLabel>Who produces this?</FieldLabel>
                                    <Radio.Group
                                        value={locationType}
                                        onChange={handleLocationTypeChange}
                                        // Who makes it is the order's identity; changing it is a new order.
                                        readOnly={isEditing}
                                    >
                                        <Group gap={24} mt={10}>
                                            <Radio value="VENDOR" label="A manufacturer" />
                                            <Radio value="WAREHOUSE" label="Our warehouse" />
                                        </Group>
                                    </Radio.Group>
                                </div>
                                <div className={classes.orderNumberSlot}>
                                {/*
                                 * Free-form, and blank is the normal case. A brand that carries
                                 * its own numbering from another system types it here; everyone
                                 * else leaves it alone and the server allocates the next one.
                                 *
                                 * "+ Prefix" is the same door "New manufacturer" is: the moment
                                 * somebody discovers they need one is mid-order, and Settings
                                 * would cost them the order they were writing.
                                 */}
                                <Group justify="space-between" align="baseline" gap={8}>
                                    <FieldLabel>Production order #</FieldLabel>
                                    {!isEditing && (
                                        <button
                                            type="button"
                                            className={classes.createLink}
                                            onClick={() => setIsAddingPrefix(true)}
                                        >
                                            <PlusIcon size={10} weight="bold" />
                                            Prefix
                                        </button>
                                    )}
                                </Group>
                                {/*
                                  * THE STEM AND THE NUMBER, when the brand has defined stems.
                                  *
                                  * Picking one fills the rest in, which is the whole point - the
                                  * number after BANGE- is not something anybody should have to
                                  * look up. It stays editable afterwards: a brand carrying a
                                  * number from another system types it straight in, exactly as
                                  * this field always allowed.
                                  *
                                  * Always rendered, even before any stem exists - the empty
                                  * dropdown holds only "+ Add prefix", which is the door to
                                  * the feature; a brand that has never seen it cannot want it.
                                  */}
                                {isEditing ? (
                                    // The number is what both sides quote; it does not change
                                    // under an edit.
                                    <TextInput value={code} readOnly className={classes.numberValue} />
                                ) : (
                                    <Group gap={6} wrap="nowrap" align="flex-start">
                                        <Select
                                            placeholder="Prefix"
                                            data={[
                                                ...prefixes.map((entry) => entry.value.trim()),
                                                { value: ADD_PREFIX_VALUE, label: '+ Add prefix' },
                                            ]}
                                            value={numberPrefix}
                                            onChange={(picked) => {
                                                // The last row is a door, not a prefix - same
                                                // pattern as the manufacturer picker's.
                                                if (picked === ADD_PREFIX_VALUE) {
                                                    setIsAddingPrefix(true);
                                                    return;
                                                }
                                                applyPrefix(picked);
                                            }}
                                            clearable
                                            comboboxProps={{ width: 220, position: 'bottom-start' }}
                                            className={classes.numberPrefix}
                                        />
                                        <TextInput
                                            placeholder="Auto-generated"
                                            value={code}
                                            onChange={(e) => setCode(e.currentTarget.value)}
                                            className={classes.numberValue}
                                        />
                                    </Group>
                                )}
                                </div>
                            </Group>
                        </div>

                        <div>
                            <Group justify="space-between" align="baseline" gap={8}>
                                <FieldLabel required>
                                    {isOutsourced ? 'Manufacturer' : 'Warehouse'}
                                </FieldLabel>
                                {/*
                                  * The way out of a dead end, next to the dead end.
                                  *
                                  * A manufacturer that is not on the list used to end the task:
                                  * the picker said "Nothing found" and the only way forward was
                                  * to abandon the order, go to Entity Master, add the vendor and
                                  * start again. This is the same Add vendor form, opened here.
                                  */}
                                {/*
                                  * A PLAIN BUTTON, not a Mantine one. The theme sets
                                  * `Button.extend({ styles: { root: { height: '2.25rem',
                                  * fontSize: '0.875rem' } } })`, and Mantine applies theme
                                  * `styles` as INLINE styles — which `size="compact-xs"` cannot
                                  * outrank, so the link rendered as a full-height 14px button
                                  * looming over the field it belongs to. Styling a bare element
                                  * is cheaper than fighting that with !important.
                                  */}
                                {isOutsourced && !isEditing && (
                                    <button
                                        type="button"
                                        className={classes.createLink}
                                        onClick={() => setIsCreatingVendor(true)}
                                    >
                                        <PlusIcon size={10} weight="bold" />
                                        New manufacturer
                                    </button>
                                )}
                            </Group>
                            <Select
                                placeholder={
                                    isOutsourced ? 'Search manufacturers' : 'Search warehouses'
                                }
                                data={locationOptions}
                                value={locationId}
                                onChange={isOutsourced ? handleVendorPicked : setLocationId}
                                disabled={isEditing}
                                searchable
                                clearable
                                searchValue={locationSearch}
                                onSearchChange={setLocationSearch}
                                /*
                                 * PINNED TO THE FIELD. Left to itself the dropdown was floating
                                 * off to the right of the box that opened it, so the list of
                                 * manufacturers had no visual connection to what had been typed.
                                 * `width: 'target'` matches the input's width and 'bottom-start'
                                 * its left edge.
                                 */
                                comboboxProps={{ width: 'target', position: 'bottom-start' }}
                                /*
                                 * SILENT UNTIL SOMETHING HAS BEEN TYPED. With an empty box there
                                 * is no "that name" to have failed to find, and the message was
                                 * appearing the moment the list was slow or empty — telling
                                 * somebody their manufacturer does not exist before they had
                                 * asked about one.
                                 */
                                nothingFoundMessage={
                                    locationSearch.trim() ? (
                                        <Text size="11px" c="dimmed" ta="left">
                                            No match for &quot;{locationSearch.trim()}&quot;
                                        </Text>
                                    ) : undefined
                                }
                            />
                        </div>

                        <Group grow>
                            <div>
                                <FieldLabel>Order date</FieldLabel>
                                <DateSinglePicker
                                    value={orderDate}
                                    onChange={(v) => setOrderDate(v || todayIso())}
                                    placeholder="Pick a date"
                                />
                            </div>
                            <div>
                                <FieldLabel required>Complete by</FieldLabel>
                                <DateSinglePicker
                                    value={dueDate}
                                    onChange={(v) => {
                                        setDueDate(v || '');
                                        // A typed date is a promise; a cleared one re-opens
                                        // the seat for the lane to fill.
                                        setCompleteByPicked(Boolean(v));
                                    }}
                                    placeholder="Pick the promised date"
                                />
                            </div>
                            {isOutsourced && (
                                <div>
                                    <FieldLabel>Currency</FieldLabel>
                                    <Select
                                        data={CURRENCY_OPTIONS}
                                        value={currency}
                                        onChange={(v) => setCurrency(v ?? 'INR')}
                                        allowDeselect={false}
                                        searchable
                                    />
                                </div>
                            )}
                        </Group>
                    </div>

                    <SectionHeader>ADD PRODUCTS</SectionHeader>

                    {isOutsourced && locationId && mappedCount > 0 && (
                        <Checkbox
                            size="xs"
                            mb={8}
                            checked={showAllProducts}
                            onChange={(e) => setShowAllProducts(e.currentTarget.checked)}
                            label={`Show all products (${mappedCount} mapped to this manufacturer)`}
                        />
                    )}
                    {isOutsourced && locationId && mappedCount === 0 && !vendorProductsQuery.isLoading && (
                        <Text size="11px" c="dimmed" mb={8}>
                            No active products mapped to this manufacturer — showing the full
                            catalogue.
                        </Text>
                    )}

                    {/*
                      * NAMES FOR THE LINE COLUMNS, which until now had none — Qty, Rate and GST %
                      * were placeholder text inside identical-looking boxes, so nothing on screen
                      * said that the first two of the four are the ones that matter. Written once
                      * above the rows rather than on every row: four labels repeated down a list
                      * of lines is noise, and the columns do not move.
                      */}
                    <div className={`${classes.lineRow} ${classes.lineHeader}`}>
                        <div className={classes.lineProduct}>
                            <FieldLabel required>Product</FieldLabel>
                        </div>
                        <div className={lineQtyClass}>
                            <FieldLabel required>Qty</FieldLabel>
                        </div>
                        {isOutsourced && (
                            <>
                                <div className={classes.lineMoney}>
                                    <FieldLabel>Rate</FieldLabel>
                                </div>
                                <div className={classes.lineGst}>
                                    <FieldLabel>GST %</FieldLabel>
                                </div>
                            </>
                        )}
                        {/* Stands in for the remove button, so the labels sit over their own
                            inputs rather than shifted left by its width. */}
                        <div className={classes.lineActionSpacer} aria-hidden="true" />
                    </div>

                    <div className={classes.lineTable}>
                        {lines.map((line, index) => (
                            <div className={classes.lineRow} key={index}>
                                <Select
                                    placeholder="Search and pick a product"
                                    data={productOptions}
                                    value={line.productId || null}
                                    onChange={(v) => handleProductPicked(index, v)}
                                    searchable
                                    clearable
                                    nothingFoundMessage="No products found"
                                    // The hint lives in the dropdown only; the box echoes the label.
                                    renderOption={({ option }) => (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%' }}>
                                            <span>{option.label}</span>
                                            {option.disabled ? (
                                                <span style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap' }}>on this order</span>
                                            ) : null}
                                        </div>
                                    )}
                                    className={classes.lineProduct}
                                />
                                <QuantityInput
                                    placeholder="Qty"
                                    value={line.quantity === '' ? '' : Number(line.quantity)}
                                    onChange={(quantity) => updateLine(index, { quantity })}
                                    unitsPerCase={productMeta.get(line.productId)?.unitsPerCase ?? null}
                                    min={0}
                                    hideControls
                                    className={lineQtyClass}
                                />
                                {isOutsourced && (
                                    <>
                                        <NumberInput
                                            placeholder={line.noAgreedPrice ? 'No agreed rate' : line.priceNote && line.unitPrice === '' ? line.priceNote : 'Rate'}
                                            title={
                                                [line.priceNote, line.priceDetail].filter(Boolean).join(' · ') ||
                                                (line.noAgreedPrice ? 'This site has no agreed rate on file for this product' : undefined)
                                            }
                                            value={line.unitPrice}
                                            onChange={(v) =>
                                                // A typed rate is the user's: it stops following the agreed prices.
                                                updateLine(index, {
                                                    unitPrice: v ?? '',
                                                    priceAuto: false,
                                                    priceNote: undefined,
                                                    priceWarn: false,
                                                    priceDetail: undefined,
                                                })
                                            }
                                            min={0}
                                            decimalScale={2}
                                            hideControls
                                            className={classes.lineMoney}
                                        />
                                        <NumberInput
                                            placeholder="GST %"
                                            value={line.gstRate}
                                            onChange={(v) =>
                                                updateLine(index, {
                                                    gstRate: v ?? '',
                                                })
                                            }
                                            min={0}
                                            max={100}
                                            hideControls
                                            className={classes.lineGst}
                                        />
                                    </>
                                )}
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => removeLine(index)}
                                    aria-label="Remove line"
                                    // Centred on the 36px input line now that the row top-aligns: the
                                    // icon is 28px, so 4px of margin puts it where centring did.
                                    mt={4}
                                >
                                    <XIcon size={14} />
                                </ActionIcon>
                            </div>
                        ))}
                    </div>

                    <Button
                        size="xs"
                        variant={ButtonVariant.SUBTLE}
                        leftSection={<PlusIcon size={14} />}
                        onClick={() => setLines((prev) => [...prev, emptyLine()])}
                        mt={8}
                    >
                        Add line
                    </Button>

                    {duplicateSku && (
                        <Text size="11px" c="red" mt={8}>
                            {duplicateSku} appears twice — one line per SKU.
                        </Text>
                    )}
                    {missingBomLines.length > 0 && (
                        <Text size="11px" c="red" mt={8}>
                            {missingBomLines.map((l) => l.productId).join(', ')} —{' '}
                            {missingBomLines.length === 1 ? 'this needs' : 'these need'} a BOM to
                            be made in your own warehouse.
                        </Text>
                    )}

                    {isOutsourced && (
                        <>
                            <Group justify="space-between" align="baseline" gap={8}>
                                <SectionHeader>MANUFACTURER</SectionHeader>
                                {manufacturerIsFilled && !isEditingManufacturer && (
                                    <Button
                                        size="compact-xs"
                                        variant={ButtonVariant.SUBTLE}
                                        onClick={() => setIsEditingManufacturer(true)}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Group>

                            {/*
                              * FOUR BOXES, OR ONE LINE OF PROSE.
                              *
                              * Once the master has answered, these four inputs are four things
                              * nobody is going to change, sitting between the reader and the rest
                              * of a long form. Collapsed they are a sentence confirming who the
                              * order is addressed to, which is the only question they answer at
                              * this point; Edit puts them back.
                              *
                              * Still open whenever anything is missing, because a summary of
                              * blanks hides work rather than saving it.
                              */}
                            {manufacturerIsFilled && !isEditingManufacturer ? (
                                /*
                                 * Set like the letterhead reads: who, then where, then the
                                 * wires. One run-on line wrapped mid-address and read like
                                 * an accident next to the form's labelled boxes.
                                 */
                                <Stack gap={2}>
                                    <Text size="sm" fw={500}>
                                        {manufacturerName}
                                    </Text>
                                    {manufacturerAddress && (
                                        <Text
                                            size="12px"
                                            c="dimmed"
                                            className={classes.summaryAddress}
                                        >
                                            {manufacturerAddress}
                                        </Text>
                                    )}
                                    {(manufacturerEmail || manufacturerGstin) && (
                                        <Text size="12px" c="dimmed">
                                            {[
                                                manufacturerEmail,
                                                manufacturerGstin
                                                    && `GST ${manufacturerGstin}`,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </Text>
                                    )}
                                </Stack>
                            ) : (
                                <div className="space-y-2">
                                    <TextInput
                                        placeholder="Name"
                                        value={manufacturerName}
                                        onChange={(e) =>
                                            setManufacturerName(e.currentTarget.value)
                                        }
                                    />
                                    <TextInput
                                        placeholder="Email"
                                        value={manufacturerEmail}
                                        onChange={(e) =>
                                            setManufacturerEmail(e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        placeholder="Address"
                                        value={manufacturerAddress}
                                        onChange={(e) =>
                                            setManufacturerAddress(e.currentTarget.value)
                                        }
                                        autosize
                                        minRows={2}
                                    />
                                    <TextInput
                                        placeholder="GST number"
                                        value={manufacturerGstin}
                                        onChange={(e) =>
                                            setManufacturerGstin(e.currentTarget.value)
                                        }
                                    />
                                </div>
                            )}

                            <SectionHeader>BILL TO</SectionHeader>
                            <div className="space-y-2">
                                <TextInput
                                    placeholder="Business name"
                                    value={billToName}
                                    onChange={(e) => setBillToName(e.currentTarget.value)}
                                />
                                <Textarea
                                    placeholder="Address"
                                    value={billToAddress}
                                    onChange={(e) => setBillToAddress(e.currentTarget.value)}
                                    autosize
                                    minRows={2}
                                />
                                <TextInput
                                    placeholder="GST number"
                                    value={billToGstin}
                                    onChange={(e) => setBillToGstin(e.currentTarget.value)}
                                />
                            </div>

                            <SectionHeader>SHIP TO</SectionHeader>
                            <div className="space-y-2">
                                <Select
                                    placeholder="Pick one of your warehouses (optional)"
                                    data={shipToOptions}
                                    value={shipToWarehouseId}
                                    onChange={handleShipToWarehouse}
                                    searchable
                                    clearable
                                    comboboxProps={{ width: 'target', position: 'bottom-start' }}
                                    nothingFoundMessage="No warehouses found"
                                />
                                <TextInput
                                    placeholder="Name"
                                    value={shipToName}
                                    onChange={(e) => setShipToName(e.currentTarget.value)}
                                />
                                <TextInput
                                    placeholder="Email"
                                    value={shipToEmail}
                                    onChange={(e) => setShipToEmail(e.currentTarget.value)}
                                />
                                <Textarea
                                    placeholder="Address"
                                    value={shipToAddress}
                                    onChange={(e) => setShipToAddress(e.currentTarget.value)}
                                    autosize
                                    minRows={2}
                                />
                                <TextInput
                                    placeholder="GST number"
                                    value={shipToGstin}
                                    onChange={(e) => handleShipToGstin(e.currentTarget.value)}
                                />
                                {/*
                                 * The place of supply, which decides whether the document shows
                                 * IGST or CGST + SGST. Chosen rather than read off the picked
                                 * warehouse: `warehouses.state` is free text, three-quarters
                                 * blank and inconsistently cased, and a tax treatment must not
                                 * turn on that. Left unset the document prints one plain "Tax"
                                 * line, which is the honest answer to nobody having said.
                                 *
                                 * Filled in from the Ship To GSTIN as it is typed, since the
                                 * state code is the first two digits of it — but picking one
                                 * here takes over for good.
                                 */}
                                <Select
                                    placeholder="Place of supply (state) — sets IGST vs CGST+SGST"
                                    data={GST_STATE_OPTIONS}
                                    value={shipToStateCode}
                                    onChange={(value) => {
                                        setPlaceOfSupplyPicked(true);
                                        setShipToStateCode(value);
                                    }}
                                    searchable
                                    clearable
                                    nothingFoundMessage="No matching state"
                                />
                            </div>

                            <SectionHeader>CONTACT</SectionHeader>
                            <div className="space-y-2">
                                <TextInput
                                    placeholder="Name"
                                    value={contactName}
                                    onChange={(e) => setContactName(e.currentTarget.value)}
                                />
                                <TextInput
                                    placeholder="Email"
                                    value={contactEmail}
                                    onChange={(e) => setContactEmail(e.currentTarget.value)}
                                />
                            </div>
                        </>
                    )}

                    <SectionHeader>NOTES {isOutsourced ? '& TERMS' : ''}</SectionHeader>
                    <div className="space-y-2">
                        <Textarea
                            placeholder="Anything the production team should know"
                            value={notes}
                            onChange={(e) => setNotes(e.currentTarget.value)}
                            autosize
                            minRows={2}
                        />
                        {isOutsourced && (
                            <Textarea
                                placeholder="Terms & conditions"
                                value={terms}
                                onChange={(e) => setTerms(e.currentTarget.value)}
                                autosize
                                minRows={3}
                            />
                        )}
                    </div>

                    <SectionHeader>ATTACHMENTS</SectionHeader>
                    <Group gap={8}>
                        <FileButton onChange={(files) => setAttachments(files ?? [])} multiple>
                            {(props) => (
                                <Button
                                    {...props}
                                    size="xs"
                                    variant={ButtonVariant.DEFAULT}
                                    leftSection={<PaperclipIcon size={14} />}
                                >
                                    Add files
                                </Button>
                            )}
                        </FileButton>
                        {attachments.length > 0 && (
                            <Text size="11px" c="dimmed">
                                {attachments.map((f) => f.name).join(', ')}
                            </Text>
                        )}
                    </Group>
                </>
            }
        />

        {/*
          * THE SAME FORM ENTITY MASTER USES, opened from here.
          *
          * Reused whole rather than reimplemented as a slimmer panel: it already knows the
          * merchant's own vendor types and cities, validates the code and email, and calls the
          * one create endpoint. A second, friendlier version of it would be a second set of
          * rules about what a vendor needs, and they would drift.
          *
          * On success the new manufacturer is selected and its details fill the block, so the
          * order carries on from where it stopped instead of sending somebody back to re-pick.
          */}
        <AddPrefixModal
            opened={isAddingPrefix}
            onClose={() => setIsAddingPrefix(false)}
            documentType="PRODUCTION_ORDER"
            existing={prefixes}
            onSaved={(saved) => {
                // The picker reads prefixes off this query, so it must learn the new one
                // before being told to select it; applyPrefix then fills the number in.
                void queryClient.invalidateQueries({ queryKey: ['org-config', organizationId] });
                applyPrefix(saved);
            }}
        />

        <AddVendorModal
            opened={isCreatingVendor}
            onClose={() => setIsCreatingVendor(false)}
            onCreated={(vendor) => {
                setIsCreatingVendor(false);
                // The picker reads from this query, so it has to know about the new row before
                // being told to select it.
                void queryClient.invalidateQueries({ queryKey: ['wo-vendors'] });
                setLocationId(vendor.vendorId);
                applyVendorDetails(vendor);
            }}
        />
        </>
    );
};

export default NewWorkOrderModal;
